import moment from 'moment-timezone';
import {useEffect, useState} from 'react';
import {Modal} from 'react-bootstrap';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {calendar} from '../../assets/imageDir';
import {BOOKING_TYPES} from '../../common/config';
import {
  addClassBookingDetails,
  addSelectedPackage,
  authCompanyData,
  clearCompanyData,
  redirectPublicUser,
} from '../../redux/actions/index';
import {BASE_URL_WEB, IMAGE_URL} from '../../utility/constants';
import {
  AxiosHelperPortal,
  calcClassRemainingSpots,
  openInNewTab,
  showToast,
} from '../../utility/helperFunctions';
import {ToastNotification} from '../index';
import useQuery from '../../hooks/useQuery';
import {classCancellationError, clientCanCancelCamp} from '../../utility/cancellationPolicy';
import {
  canProgressFromMultiDayCampLink,
  getMultiDayCampAvailability,
  linkedCampOccurrenceIsUnavailable,
} from '../../utility/campBooking';

const EventsClassModal = ({show, onHide, details}) => {
  console.log('class details ==> ', details);
  const branchIdUrlQuery = useQuery().get('branchId');
  const history = useHistory();
  const authDetails = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const timezone =
    authDetails?.companyData?.branch?.timezone ||
    authDetails?.companyData?.branches?.items?.find(branch => branch?.id === branchIdUrlQuery)
      ?.timezone;
  const isMultiDayCamp = Boolean(details?.isCamp && details?.repeat_id);
  const multiDayCampAvailability = getMultiDayCampAvailability(details);
  const linkedOccurrenceUnavailable = linkedCampOccurrenceIsUnavailable(details);

  const [cancelClassLoading, setCancelClassLoading] = useState(false);

  const [isClassFull, setIsClassFull] = useState(false);

  // NEW: controls whether the "confirm cancellation" modal is shown
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);

  useEffect(() => {
    if (isMultiDayCamp && multiDayCampAvailability) {
      setIsClassFull(multiDayCampAvailability.days.length === 0);
    } else if (isMultiDayCamp) {
      setIsClassFull(false);
    } else if (calcClassRemainingSpots(details) === 'Class Full') {
      setIsClassFull(true);
    } else {
      setIsClassFull(false);
    }
  }, [details, authDetails?.companyData, isMultiDayCamp, multiDayCampAvailability]);

  useEffect(() => {
    const member_services = authDetails?.companyData?.member_service;
    let new_member_service = null;
    if (member_services?.length != null && member_services.length > 0) {
      new_member_service = member_services.filter(
        e => e.typename === 'membership_plan_subscribers'
      );
    }
    if (new_member_service === null || details?.serviceId == null) {
      getRemainingSpots();
    }
  }, [details, authDetails?.companyData]);

  // Fetch remaining spots for multi-class packs or single service credits
  const getRemainingSpots = () => {
    let remaining = 0;
    const member_services = authDetails?.companyData?.member_service;
    const multi_class_pack = authDetails?.companyData?.services?.multi_class_pack;

    if (member_services?.length != null && member_services.length > 0) {
      let new_member_service = member_services.filter(
        e =>
          (e.typename === 'member_multi_class_pack' &&
            e?.ptRemaining != null &&
            e.ptRemaining > 0 &&
            multi_class_pack.length != null &&
            multi_class_pack.length > 0 &&
            multi_class_pack.find(
              m =>
                (m.uuid === e.multiClassPackId && m?.isAllClassPack === true) ||
                (m?.selectedClasses?.length != null &&
                  m?.selectedClasses?.length > 0 &&
                  m?.selectedClasses.includes(details?.serviceId))
            )) ||
          (e.typename === 'member_service' &&
            e?.ptRemaining != null &&
            e.ptRemaining > 0 &&
            e.serviceId === details?.serviceId)
      );

      if (new_member_service?.length != null && new_member_service?.length > 0) {
        remaining = new_member_service.reduce((total, val) => {
          if (val?.ptRemaining != null) {
            return total + val.ptRemaining;
          }
          return total;
        }, 0);
      }
    }

    return remaining;
  };

  // ================================================
  // 1) Extracted cancellation logic to a new function
  // ================================================
  const cancelClass = async () => {
    setCancelClassLoading(true);
    if (!clientCanCancelCamp(details)) {
      setCancelClassLoading(false);
      showToast.error('Please contact the coach to cancel this registration.');
      return;
    }
    const cancellationError = classCancellationError({
      companyData: authDetails?.companyData,
      booking: details,
    });
    if (cancellationError) {
      setCancelClassLoading(false);
      showToast.error(cancellationError);
      return;
    }
    const memberId = authDetails?.companyData?.member?.id;
    const payload = {
      cancelAllAppointments: null,
      cancelType: 'session_credit',
      cancelledReason: 'Cancelled by client',
      groupClassId: details?.uuid,
      partialRefundAmount: null,
      paymentMethod: 'pay_now_cash',
      refundType: null,
      uuid: details?.bookingId,
      memberId: memberId,
      orderId: details?.orderId,
      isGroupClass: 'yes',
      from: 'client_portal',
      fmIds: ['_parent'],
      isFromClient: true,
      start: details?.start,
    };
    console.log('payload', payload);
    const url = `${BASE_URL_WEB}appointment-cancel?`;
    console.log('url', url, payload);

    try {
      const response = await AxiosHelperPortal(url, 'post', payload);
      console.log('response', response);
      setCancelClassLoading(false);
      if (response?.data?.cancellationError === true) {
        showToast.error(response?.data?.message);
      } else {
        dispatch(authCompanyData());
        history.push({
          pathname: '/class-booking',
        });
        showToast.success('Your class is cancelled successfully.');
      }
    } catch (error) {
      console.log('err', error);
      setCancelClassLoading(false);
      showToast.error(error.response?.data?.message);
    }
  };

  // ================================================
  // 2) Modified onBook to show confirmation modal
  // ================================================
  const onBook = () => {
    // If user is about to cancel
    if (
      details?.isBooked &&
      details.typename !== BOOKING_TYPES.APPOINTMENT &&
      details?.familyMembers?.length === 0 &&
      clientCanCancelCamp(details)
    ) {
      // Show "Are you sure you want to cancel?" modal
      setShowCancelConfirmModal(true);
    } else {
      // Otherwise, continue booking flow
      dispatch(addSelectedPackage(null));
      details.isClassFull = isClassFull;
      dispatch(addClassBookingDetails(details));
      history.push({
        pathname: '/confirm-booking',
      });
    }
  };

  // If the user is not logged in, redirect them to login
  const onBookPublicMoveToLoginPage = () => {
    dispatch(addSelectedPackage(null));
    dispatch(clearCompanyData());
    dispatch(
      redirectPublicUser({
        companyId: authDetails?.companyData?.id,
        companyName: authDetails?.companyData?.name,
        classId: details?.uuid,
        branchDetails: authDetails?.companyData?.branches?.items,
        imageUrl: authDetails?.companyData?.imageUrl,
        showKidsAfterSignup: authDetails?.companyData?.showKidsAfterSignup,
        eventDate: details?.start_branch_time,
        haveKids: details?.haveKids,
      })
    );
    history.push('/login');
  };

  // Decide the text for the main button
  const showContinueBtnText = () => {
    let txt = 'Continue Booking';

    const isBookedIsCampNoBooking = details?.isBooked && details?.isCamp;
    if (isBookedIsCampNoBooking) {
      txt = 'View Details';
    } else if (
      details?.isBooked &&
      details.typename !== BOOKING_TYPES.APPOINTMENT &&
      details?.familyMembers?.length === 0
    ) {
      txt = 'Cancel Registration';
    } else if (details?.isBooked && details.typename !== BOOKING_TYPES.APPOINTMENT) {
      txt = 'Update Registration';
    }
    return txt;
  };
  // Convert registration dates to branch timezone for proper comparison and display
  const registrationOpenDate = details?.reg_open_Date
    ? moment.tz(details.reg_open_Date, timezone)
    : moment.tz(details?.createdAt, 'UTC');
  const registrationCloseDate = details?.reg_close_Date
    ? moment.tz(details.reg_close_Date, timezone)
    : moment.tz(details?.start_branch_time, timezone);
  const currentTime = moment.tz(timezone);
  const canContinueBooking =
    canProgressFromMultiDayCampLink(details) ||
    (registrationOpenDate?.isSameOrBefore(currentTime) &&
      registrationCloseDate?.isSameOrAfter(currentTime));

  console.log(
    'registrationCloseDate',
    registrationCloseDate?.format(),
    details?.reg_close_Date,
    details?.start_branch_time
  );
  return (
    <>
      {/* Existing modal for class details */}
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="class-info-modal"
      >
        <div className="modal-content-inner">
          <div className="modal-header">
            <h5 className="modal-title">{details?.isCamp ? 'Camp Details' : 'Class Details'}</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={onHide}
            ></button>
          </div>
          <div className="modal-body">
            {isClassFull && (
              <div
                style={{
                  backgroundColor: '#f8d7da',
                  padding: '5px',
                  borderRadius: '4px',
                  fontFamily: 'Arial, sans-serif',
                }}
              >
                <h6 className="text-danger">{details.isCamp ? 'Camp Full' : 'Class Full'}</h6>
              </div>
            )}
            <div className="classInfoBlock">
              <figure className="classInfo-img">
                {details?.imageUrl && (
                  <img src={IMAGE_URL + details?.imageUrl} alt="" className="img-fluid" />
                )}
              </figure>

              <div
                style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  padding: '10px',
                  paddingBottom: '30px',
                }}
              >
                <p className="service text-dark _name _title-3" style={{fontWeight: '500'}}>
                  <span className="icon-holder-txt">
                    {details?.virtualSession && <i className="fas fa-video"></i>}
                    {details?.isCamp && details?.commonT
                      ? details?.commonT
                      : details?.serviceName}, {details?.branchName}
                  </span>
                </p>

                <pre className="desc-normal">
                  {details?.isCamp && details?.commonDes
                    ? details?.commonDes
                    : details?.description}
                </pre>

                {details?.zoomLink && details?.isBooked && (
                  <div className="zoomLink">
                    <p className="_name _title-3 mr-2">Zoom Link:</p>
                    <span
                      className="iconRound"
                      onClick={() => openInNewTab(details?.zoomLink)}
                      onKeyPress={null}
                      role="link"
                      tabIndex={-1}
                    >
                      <i className="bx bx-video"></i>
                    </span>
                  </div>
                )}
              </div>

              {!details?.onlineBooking &&
                typeof details?.access !== 'undefined' &&
                details?.access === 'private' && (
                  <div className="text-center m-3">
                    <span className="text-danger font-weight-bold">
                      Sorry ! This class is not available for online booking.
                    </span>
                  </div>
                )}
              {details?.status === 'cancelled' && !isMultiDayCamp && (
                <div className="text-center m-3">
                  <span className="text-danger font-weight-bold">
                    Sorry! This class is cancelled.
                  </span>
                </div>
              )}

              <div className="date-time-div" style={{marginTop: '30px'}}>
                <div className="date-div">
                  <span className="_icon">
                    <img src={calendar} alt="" />
                  </span>
                  {moment(details?.start_branch_time).format('dddd, MMMM Do YYYY')}
                </div>
                <div className="time-div">
                  <span className="_icon">
                    <i className="far fa-clock"></i>
                  </span>
                  {moment(details?.start_branch_time).format('hh:mm A')}
                </div>
              </div>
            </div>

            <div className="cinfo-div-outter mb-5 mt-3">
              <div className="cinfo-div mb-2">
                <span className="cinfo-title">
                  <i className="bx bx-user"></i> Coaches
                </span>
                <span className="cinfo">
                  {details?.selectedTrainers?.map((trainer, index) => (
                    <span key={trainer?.id}>
                      {trainer?.trainerName}
                      {details?.selectedTrainers?.length - 1 !== index && ', '}
                    </span>
                  ))}
                </span>
              </div>
              <div className="cinfo-div mb-2">
                <span className="cinfo-title">
                  <i className="bx bx-user"></i> Booked Members
                </span>
                <span className="cinfo">
                  {details?.familyMembers?.map((member, index) => (
                    <span key={`${member?.orderId}-${index}`}>
                      {member?.isParent
                        ? authDetails?.companyData?.member?.name
                        : `${member?.firstName} ${member?.lastName}`}
                      {details?.familyMembers?.length - 1 !== index && ', '}
                    </span>
                  ))}
                </span>
              </div>

              {/* {memberService !== null &&
                membership_pack == null &&
                authDetails?.authDetails &&
                !groupClassDetail[0]?.campDaysBookTogether &&
                !groupClassDetail[0]?.isCamp && (
                  <div className="cinfo-div">
                    <span className="cinfo-title">
                      <i className="bx bx-money"></i>Credits Remaining for22 {details?.serviceName}:{' '}
                    </span>
                    <span className="cinfo">{loading ? 'Loading.....' : remainingCredits}</span>
                  </div>
                )}
              {membership_pack != null && (
                <div className="cinfo-div">
                  <span className="cinfo-title">
                    <i className="bx bx-money"></i>Credits Remaining for {details?.serviceName}:{' '}
                  </span>
                  <span className="cinfo">
                    {loading === true && 'Loading.....'}
                    {isUnlimited === true && loading !== true && 'Unlimited membership pack active'}
                    {isUnlimited !== true && loading !== true && remainingCredits}
                  </span>
                </div>
              )} */}
              {/* //! Registration details */}
              <>
                <br />
                {!isMultiDayCamp &&
                  registrationOpenDate &&
                  !registrationOpenDate?.isSameOrBefore(currentTime) && (
                    <p
                      className="text font-size-16 font-weight-bold font-italic _text-theme"
                      style={{marginBottom: '30px'}}
                    >
                      Registration Opens:{' '}
                      <span className="font-weight-normal">
                        {registrationOpenDate?.format('dddd, MMMM D, YYYY h:mm a z')}
                      </span>
                    </p>
                  )}
                {!isMultiDayCamp && registrationCloseDate?.isSameOrAfter(currentTime) && (
                  <div style={{marginTop: '10px', marginBottom: '30px'}}>
                    <p className="text font-size-16 font-weight-bold font-italic mt-2">
                      Registration Closes:{' '}
                      <span className="font-weight-normal">
                        {registrationCloseDate?.format('dddd, MMMM D, YYYY h:mm a z')}
                      </span>
                    </p>
                  </div>
                )}
                {/* //! Show "Registration now closed" if current time is after reg_close_Date */}
                {!isMultiDayCamp && currentTime.isAfter(registrationCloseDate) && (
                  <p
                    className="text font-size-16 font-weight-bold font-italic _text-theme"
                    style={{marginBottom: '30px'}}
                  >
                    Registration now closed
                  </p>
                )}
                {isMultiDayCamp && linkedOccurrenceUnavailable && (
                  <p
                    className="text font-size-16 font-weight-bold font-italic _text-theme"
                    style={{marginBottom: '15px'}}
                  >
                    Registration is closed for this camp day (
                    {moment(details?.start_branch_time).format('MMMM Do, YYYY')}).
                  </p>
                )}
                {isMultiDayCamp && multiDayCampAvailability?.days?.length > 0 && (
                  <p
                    className="text font-size-16 font-weight-bold font-italic"
                    style={{marginBottom: '30px'}}
                  >
                    Camp days available to book:{' '}
                    <span className="font-weight-normal">
                      {moment(multiDayCampAvailability.startsAt).format('MMMM Do, YYYY hh:mm A')} -{' '}
                      {moment(multiDayCampAvailability.endsAt).format('MMMM Do, YYYY hh:mm A')}
                    </span>
                  </p>
                )}
                {isMultiDayCamp && multiDayCampAvailability?.days?.length === 0 && (
                  <p
                    className="text font-size-16 font-weight-bold font-italic _text-theme"
                    style={{marginBottom: '30px'}}
                  >
                    There are no camp days currently available to book.
                  </p>
                )}
                <br />
              </>
              <br />
            </div>

            {/* Action Button Section */}
            {!isClassFull ? (
              <>
                {(details?.status !== 'cancelled' || isMultiDayCamp) && (
                  <div className="btn-div text-center mt-4 mb-3">
                    {authDetails.authDetails
                      ? canContinueBooking && (
                          <button className="btn _btnRound" onClick={onBook} style={{top: '85%'}}>
                            {showContinueBtnText()}
                          </button>
                        )
                      : canContinueBooking && (
                          <button
                            className={'btn _btnRound'}
                            onClick={onBookPublicMoveToLoginPage}
                            style={{backgroundColor: '#c82638', color: 'white', top: '85%'}}
                          >
                            Continue Booking
                          </button>
                        )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="btn-div text-center mt-4 mb-3">
                  {details.haveKids &&
                  details.haveKids === true &&
                  details.familyMembers?.length > 0 ? (
                    <button className="btn _btnRound" onClick={onBook}>
                      Update Registration
                    </button>
                  ) : (
                    <button className="btn _btnRound" disabled={true}>
                      {isMultiDayCamp
                        ? 'No Available Camp Days'
                        : details.isCamp
                        ? 'Camp Full'
                        : 'Class Full'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {cancelClassLoading && (
          <div className="overlay">
            <div className="spinner"></div>
          </div>
        )}
      </Modal>

      {/* NEW: "Are you sure you want to cancel?" Modal */}
      <Modal show={showCancelConfirmModal} onHide={() => setShowCancelConfirmModal(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">Cancel Class</h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setShowCancelConfirmModal(false)}
          ></button>
        </div>
        <div className="modal-body">
          <p>Are you sure you want to cancel your class?</p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowCancelConfirmModal(false)}
          >
            No
          </button>
          <button type="button" className="btn btn-danger" onClick={cancelClass}>
            Yes, Cancel
          </button>
        </div>
      </Modal>

      <ToastNotification />
    </>
  );
};

export default EventsClassModal;
